// [전체 코드 미사용/주석 삭제 금지]
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from "config/apiConfig";
import { useBranch } from 'contexts/BranchContext';

// CSS imports (Boost.jsx, Product.jsx와 동일한 환경 가정)
import 'styles/Font.css';

// Images imports [미사용/주석 삭제 금지]
import searchIcon from 'assets/images/icon/search-icon.svg';
import noImage from 'assets/images/logo/BranchNoImage.png' // 대체 기본 이미지

// Components imports (TopNavigator는 Product.jsx에서, Footer는 공통 컴포넌트로 가정)
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import Footer from "components/layout/Footer/Footer";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import { getTokenStatus } from "utils/tokenManager";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";

/**
 * 지점 목록 API를 호출하는 함수.
 */
const fetchBranchListApi = async () => {
    const API_URL = `${API_BASE_URL}/store`;
    const { token } = getTokenStatus();
    /* ----------------------------------------------------- */
    /* ------------ [ 실제 API 호출 로직 (주석 처리) ] ------------ */
    /* ----------------------------------------------------- */
    try {
        // GET /api/store 지점 목록 호출
        const response = await fetch(API_URL, {
            method: 'GET',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Authorization': `Bearer ${token}`, },
        });

        // HTTP 상태 코드가 200번대가 아닐 경우 에러 처리
        if (!response.ok) {
            throw new Error(`API 호출 실패: HTTP 상태 ${response.status}`);
        }

        const data = await response.json();
        // API 응답 구조가 { status: 'success', data: { list: [...] } } 형식이므로 data.data.list를 참조
        const list = data?.data?.list || [];

        // API 응답 구조가 { "list": [...] } 이므로 data.list를 반환
        return list;
    } catch (e) {
        console.error("API 호출 에러:", e);
        throw new Error('네트워크 오류 또는 서버 응답 문제');
    }
};

// ----------------------------------------------------------------------


function BranchList() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();

    /*================================상태관리================================*/
    const [searchText, setSearchText] = useState('');
    const [branchList, setBranchList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { selectBranch } = useBranch();

    // [!! 추가: 동적 높이 측정을 위한 ref와 state !!]
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(80);

    /*==============================동적 높이 측정===============================*/
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                // border나 padding을 포함한 실제 렌더링 높이를 측정
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        measureHeight(); // 컴포넌트 마운트 시 측정

        // 윈도우 리사이즈 시 재측정 (모바일 기기에서 주소창/상태바 크기 변동에 대비)
        // [!! requestAnimationFrame으로 성능 개선 !!]
        let animationFrame;
        const debouncedMeasure = () => {
            if (animationFrame) cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(measureHeight);
        };

        window.addEventListener('resize', debouncedMeasure);
        return () => {
            window.removeEventListener('resize', debouncedMeasure);
            if (animationFrame) cancelAnimationFrame(animationFrame);
        };
    }, []);
    /*======================================================================*/


    /*=================================데이터 로딩=================================*/
    const fetchBranchList = async () => {
        setLoading(true);
        setError(null);
        try {
            // 실제 API 호출
            const list = await fetchBranchListApi();

            // list가 배열인지 확인 후 set (API 응답 구조 안전성 확보)
            if (Array.isArray(list)) {
                // 추후 API 연동을 위한 변수(isProductAvailable) 처리
                // 위례중앙점(2)은 항상 'Y', 나머지는 API 값을 따르거나 없으면 'N'
                const processedList = list.map(branch => ({
                    ...branch,
                    isProductAvailable: branch.brKey === '2' ? 'Y' : (branch.isProductAvailable || 'N')
                }));
                setBranchList(processedList);
            } else {
                setBranchList([]);
            }

        } catch (e) {
            setError(e.message || '지점 목록을 불러오는 데 실패했습니다.');
            console.error(e);
            // 오류 발생 시 목록을 비워둠
            setBranchList([]);
        } finally {
            setLoading(false);
        }
    };

    // 컴포넌트가 마운트될 때 API 호출
    useEffect(() => {
        fetchBranchList();
    }, []);
    /*======================================================================*/


    /*=================================핸들러=================================*/
    // 검색어 변경 핸들러
    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        e.stopPropagation(); // 이벤트 버블링 방지
    };

    // 지점 선택하기 버튼 핸들러 (예시)
    const handleBrSelectClick = (branch) => {
        const selectedData = {
            brKey: branch.brKey,
            brName: branch.brName,
            isProductAvailable: branch.isProductAvailable, // 상품 가용 여부 플래그
            footerInfo: branch.footerInfo ? {
                companyName: branch.footerInfo.companyName || '',
                ceoName: branch.footerInfo.ceoName || '',
                businessNumber: branch.footerInfo.businessNumber || '',
                ecommerceNumber: branch.footerInfo.ecommerceNumber || '',
                address: branch.footerInfo.address || '',
                csPhone: branch.footerInfo.csPhone || '',
                email: branch.footerInfo.email || ''
            } : {
                companyName: branch.brCompanyName || '',
                ceoName: branch.brCeoName || '',
                businessNumber: branch.brBizNo || '',
                ecommerceNumber: branch.brMailOrderNo || '',
                address: branch.brAddr || '',
                csPhone: branch.brTel || '',
                email: branch.brEmail || ''
            }
        };
        selectBranch(selectedData);

        // 홈으로 이동 (state 전달 불필요)
        navigate('/home');
    };

    // 지점 정보 버튼 핸들러 (예시)
    const handleBrInfoClick = (brKey) => {
        // TODO: 예약/정보 페이지 이동 로직 추가
        // e.stopPropagation(); // 이벤트 버블링 방지
        navigate(`/brinfo/${brKey}`, {});
    };

    // 상품 구매 버튼 핸들러
    const handleProductPurchaseClick = (branch) => {
        // 전역 상태에 지점 정보 저장
        selectBranch({
            brKey: branch.brKey,
            brName: branch.brName,
            isProductAvailable: branch.isProductAvailable,
            footerInfo: {
                companyName: branch.brCompanyName || '하입하우스',
                ceoName: branch.brCeoName || '신혁',
                businessNumber: branch.brBizNo || '405-02-62165',
                ecommerceNumber: branch.brMailOrderNo || '2025-성남수정-0703',
                address: branch.brAddr || '경기도 성남시 수정구 위례광장로 300 (위례중앙타워)',
                csPhone: branch.brTel || '070-7717-8822',
                email: branch.brEmail || 'luhabit@naver.com'
            }
        });

        // 해당 지점의 상품 구매 페이지로 이동
        navigate(`/prod/${branch.brKey}`);
    };
    /*======================================================================*/

    /*===============================지점 리스트 렌더링===============================*/
    const renderBranchList = () => {
        // [!! 수정: branchList가 배열이 아닐 경우 빈 배열로 대체하여 filter 오류 방지 !!]
        const listToFilter = Array.isArray(branchList) ? branchList : [];

        // 검색어에 따라 목록 필터링
        const filteredList = listToFilter.filter(branch => {
            if (!searchText) return true;
            const searchLower = searchText.toLowerCase();
            // brName 또는 brAddr에 검색어가 포함되는지 확인
            return (
                branch.brName.toLowerCase().includes(searchLower) ||
                branch.brAddr.toLowerCase().includes(searchLower)
            );
        });

        // 로딩 상태
        if (loading) {
            return <p style={staticStyles.infoMessage}>지점 목록을 불러오는 중...</p>;
        }

        // 에러 상태
        if (error) {
            return (
                <div style={staticStyles.errorContainer}>
                    <p style={staticStyles.errorMessage}>{error}</p>
                    <button onClick={fetchBranchList} style={staticStyles.retryButton}>
                        다시 시도
                    </button>
                </div>
            );
        }

        // 필터링 결과 없음
        if (filteredList.length === 0) {
            return <p style={staticStyles.infoMessage}>검색 결과가 없습니다.</p>;
        }

        // 정상 목록 렌더링
        return (
            <>
                {/*<p style={staticStyles.searchDebugText}>총 **{filteredList.length}개** 지점이 검색되었습니다.</p>*/}
                <div style={staticStyles.listWrapper}>
                    {filteredList.map(branch => (
                        <div key={branch.brKey} className="branchListItem" style={staticStyles.branchListItem}>

                            <div style={staticStyles.branchContentWrapper}>

                                {/* 1. 지점 이미지 (고정 높이 88px) */}
                                <div className="branchImgDiv" style={staticStyles.branchImgDiv}>
                                    <img
                                        className="branchImg"
                                        src={branch.brImg || noImage}
                                        alt={branch.brName}
                                        style={staticStyles.branchImage}
                                    />
                                </div>

                                {/* 2. 텍스트 정보 및 버튼 컨테이너 (Flex: 1로 남은 공간 차지) */}
                                <div style={staticStyles.branchInfoTop}>

                                    {/* 2-1. 텍스트 정보 (이름, 거리, 주소) */}
                                    <div style={staticStyles.branchInfoDiv}>
                                        <div style={staticStyles.branchNameAndDistance}>
                                            <p className="branchName"
                                                style={staticStyles.branchName}>{branch.brName}</p>
                                            {/* 거리가 API에 없으므로 임시 숨김 처리 */}
                                            {/* <p className="branchDistance" style={staticStyles.branchDistance}>1.4km</p> */}
                                        </div>
                                        <span className="branchAddr"
                                            style={staticStyles.branchAddress}>{branch.brAddr}</span>
                                    </div>


                                    {/* 2-2. 하단 버튼 3개 */}
                                    <div style={staticStyles.buttonContainer}>
                                        <button
                                            style={{ ...staticStyles.actionButton, ...staticStyles.inquiryButton }}
                                            onClick={() => handleBrInfoClick(branch.brKey)}
                                        >
                                            지점 정보
                                        </button>
                                        <button
                                            style={{ ...staticStyles.actionButton, ...staticStyles.reservationButton }}
                                            onClick={() => handleBrSelectClick(branch)}
                                        >
                                            지점 선택
                                        </button>
                                        {/* TODO: 20260611 고객사 요청으로 상품구매 임시 숨김 처리 */}
                                        {/* {branch.isProductAvailable === 'Y' && (
                                            <button
                                                style={{ ...staticStyles.actionButton, ...staticStyles.purchaseButton }}
                                                onClick={() => handleProductPurchaseClick(branch)}
                                            >
                                                상품 구매
                                            </button>
                                        )} */}
                                    </div>
                                </div>
                            </div>

                        </div>
                    ))}
                </div>
            </>
        );
    };
    /*======================================================================*/

    const dynamicMainContentStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
        paddingBottom: '100px', // Footer 높이 (Footer는 staticStyles.bottomNavWrapper의 높이 100px)
        paddingLeft: '24px',
        paddingRight: '24px',
    };

    return (
        <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>
            {/* [!! ref 추가: 헤더 높이 측정을 위해 !!] */}
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar} />
                <TopNavigator title="지점 목록" />
            </div>

            {/* 2. 메인 콘텐츠 (스크롤 영역) */}
            {/* [!! 동적 스타일 적용 !!] */}
            <main style={dynamicMainContentStyle}>

                {/* 검색 입력창 컨테이너 */}
                <div style={staticStyles.searchContainer}>
                    <div style={staticStyles.searchInputWrapper}>
                        <img src={searchIcon} alt="검색" style={staticStyles.searchIcon} />
                        <input
                            type="text"
                            placeholder="지역, 지하철역, 매장명"
                            value={searchText}
                            onChange={handleSearchChange}
                            style={staticStyles.searchInput}
                        />
                    </div>
                </div>

                {/* 지점 목록 영역 */}
                <div style={staticStyles.tabContentArea}>
                    {renderBranchList()}
                </div>

                {/* 푸터 (콘텐츠와 함께 스크롤됨) */}
                <Footer />
            </main>
        </div>
    );
}

// [!! staticStyles 객체 !!]
/** @type {React.CSSProperties} */
const staticStyles = {
    // --- 1. 전체 레이아웃 (공통) ---
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh',
        display: 'flex', flexDirection: 'column', alignItems: 'stretch',
        margin: '0 auto', boxSizing: 'border-box',
        overflowX: 'hidden',
    },
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0, width: '100vw',
        zIndex: 100, backgroundColor: '#0D042D', transform: 'translate3d(0, 0, 0)',
    },
    statusBar: {
        flexShrink: 0, backgroundColor: 'transparent',
    },
    mainContent: {
        flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: '10px', boxSizing: 'border-box', width: '100%',
    },

    // --- 1.5. 검색 입력창 ---
    searchContainer: {
        width: '100%',
        padding: '0',
        boxSizing: 'border-box',
        flexShrink: 0,
    },
    searchInputWrapper: {
        width: '100%',
        height: '48px',
        background: '#1D1A3E',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        boxSizing: 'border-box',
    },
    searchIcon: {
        width: '24px',
        height: '24px',
        marginRight: '8px',
        flexShrink: 0,
    },
    searchInput: {
        flex: 1,
        background: 'transparent',
        border: 'none',
        outline: 'none',
        fontFamily: 'Pretendard',
        fontWeight: '500',
        fontSize: '16px',
        color: '#FCFCFA',
        'WebkitInputPlaceholder': { color: '#747474' },
    },

    // --- 6. 지점 목록 영역 (tabContentArea) ---
    tabContentArea: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    listWrapper: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
    },
    searchDebugText: {
        width: '100%', textAlign: 'left', color: '#A0A0A0',
        fontFamily: 'Pretendard', fontSize: '14px', fontWeight: '500',
        marginBottom: '10px',
    },

    // --- 지점 목록 아이템 (목록 형태) ---
    branchListItem: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'transparent',
        borderRadius: '0px',
        padding: '16px 0', // 상하 패딩은 유지
        borderBottom: '1px solid #1D1A3E',
        boxSizing: 'border-box',
    },

    // [!! 새로 추가된 wrapper: 이미지와 나머지 콘텐츠를 가로 정렬 !!]
    branchContentWrapper: {
        display: 'flex',
        alignItems: 'flex-start',
        height: '88px', // 전체 높이를 이미지 높이인 88px로 고정
    },

    // --- 지점 이미지 ---
    branchImgDiv: {
        width: '88px',
        height: '88px',
        borderRadius: '8px',
        overflow: 'hidden',
        marginRight: '16px',
        flexShrink: 0,
        backgroundColor: '#333',
    },
    branchImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },

    // --- 텍스트 및 버튼 (오른쪽 영역, flex: 1) ---
    branchInfoTop: {
        flex: 1, // 남은 공간 모두 차지
        display: 'flex',
        flexDirection: 'column', // 내부 요소(텍스트, 버튼)는 세로 정렬
        justifyContent: 'space-between', // 텍스트 정보와 버튼 컨테이너를 상하 끝으로 분리
        height: '100%', // 부모(branchContentWrapper)의 88px 높이를 상속
    },

    // --- 텍스트 정보 (이름, 거리, 주소) ---
    branchInfoDiv: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
    },
    branchNameAndDistance: {
        display: 'flex',
        height: '28px',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1px', // 주소와의 간격
        width: '100%', // 이름/거리 간의 space-between을 위해 너비 100% 확보
    },
    branchName: {
        fontFamily: 'Pretendard', fontWeight: '500', fontSize: '18px',
        color: '#FCFCFA',
        flexShrink: 1,
        marginRight: '16px',
    },
    branchDistance: {
        fontFamily: 'Pretendard', fontWeight: '400', fontSize: '18px',
        color: '#FCFCFA',
        flexShrink: 0,
    },
    branchAddress: {
        fontFamily: 'Pretendard', fontWeight: '400', fontSize: '12px',
        color: '#A0A0A0',
        lineHeight: '1.4', // 12px 폰트에 적합한 라인 높이
        height: '28px', // 2줄 높이 고정
        verticalAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
        display: '-webkit-box',
    },

    // --- 카드 하단 (버튼 컨테이너) ---
    buttonContainer: {
        display: 'flex',
        gap: '4px', // 3개 버튼을 위해 간격 축소
        width: '100%',
        height: '30px',
        padding: '0',
        marginTop: '1px',
        boxSizing: 'border-box',
    },
    actionButton: {
        flex: 1,
        whiteSpace: 'nowrap', // 텍스트 줄바꿈 방지
        height: '30px',
        borderRadius: '8px',
        fontFamily: 'Pretendard',
        fontWeight: '700',
        fontSize: '12px', // 3개 버튼을 위해 폰트 크기 축소 (기존 14px)
        cursor: 'pointer',
        border: 'none',
        padding: 0,
    },
    inquiryButton: {
        backgroundColor: '#4D4D6B', // 지점 정보 (어두운 회색)
        color: '#FFFFFF',
    },
    reservationButton: {
        backgroundColor: '#8052FF', // 지점 선택 (브랜드 컬러 유지)
        color: '#FFFFFF',
    },
    purchaseButton: {
        background: 'linear-gradient(90deg, #0da0b2 0%, #22c96d 100%)',
        color: '#FFFFFF',
    },


    // --- 로딩 및 에러 메시지 (기존 유지) ---
    infoMessage: {
        fontFamily: 'Pretendard', fontSize: '16px', color: '#747474',
        textAlign: 'center', marginTop: '50px',
    },
    errorContainer: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        marginTop: '50px',
    },
    errorMessage: {
        fontFamily: 'Pretendard', fontSize: '16px', color: '#FF5252',
        marginBottom: '10px',
    },
    retryButton: {
        backgroundColor: '#8052FF', color: '#FFFFFF',
        border: 'none', borderRadius: '4px', padding: '10px 15px',
        cursor: 'pointer', fontFamily: 'Pretendard', fontWeight: '600',
    },

};

export default BranchList;